import React, { useState, useEffect, useMemo } from 'react';
import { Tag, Skeleton, Stack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ProductSwapTypes } from '../../../utils/constants';

import styled from 'styled-components';
import currencyFormatter from 'currency-formatter';
import get from 'lodash/get';
import uniq from 'lodash/uniq';

import { FIXED_AMOUNT_DISCOUNT, PERCENTAGE_DISCOUNT } from '../../constants';

import {
  getCustomAttribute,
  getUnhiddenCustomAttributesString,
  hasMinQuantityItemsInSubscription,
  hasMinQuantityItemsDynamically,
  getOptimizedImageURL,
  itemIsFreeProduct,
} from '../../utils';
import { ProductOptions } from '../../ProductOptions';
import useProductContext, {
  ProductContext,
  ProductProvider,
} from '../../state/ProductContext';
import ProductDetail from '../../../components/ProductDetail';
import QuantityButton from '../../../components/QuantityButton';
import Breakpoints, {
  getBreakpointWithOffset,
} from '../../../design-utils/breakpoints';
import { PRODUCT_CHANGE_OPTIONS } from '../../../utils/constants';
import { useGlobalContext } from '../../state/GlobalContext';
import {
  getBaBLines,
  isDynamicLine,
  isClassicBabLine,
} from '../../../utils/BundleUtils';
import { ImageEmptyState } from '../../../components/ProductDetail/ProductDetail';
import {
  SC_LINE_MODS,
  SOURCE,
  scLineModAllowed,
} from '../../../../lib/scLineMods';
import { cleanDefaultDisplayName } from '../../../../components/Subscriptions/utils';
import Button from '../../../components/Button';
import CurateShuffleModal from '../../CurateShuffleModal';
import {
  isFeatureFlagEnabled,
  FEATURE_FLAGS,
} from '../../../../widgetSrc/utils/featureFlags';

const StrikedPrice = styled.span.attrs({
  className: 'bundle-striked-price',
})`
  text-decoration: line-through;
  display: block;
  font-size: 12px;
`;

const TotalLabel = styled.p`
  text-align: right;
`;

const QuantityButtonContainer = styled.div.attrs({
  className: 'bundle-quantity-button-container',
})`
  display: flex;
  @media screen and (min-width: ${getBreakpointWithOffset('sm', 1)}) {
    display: none !important;
  }
`;

const QuantityDataCell = ({
  line,
  isBaB,
  subscription,
  t,
  handleOnQuantityChange,
}) => {
  const { customerPortalSettings, ignoreBaBOutOfStockAllowance } =
    useGlobalContext();
  const isCancelled = subscription?.SubscriptionStatus === 'CANCELLED';
  const isPaused = subscription?.SubscriptionStatus === 'PAUSED';
  const isPrepaid =
    !!subscription?.BillingIntervalBillingCount &&
    !!subscription?.DeliveryIntervalDeliveryCount &&
    subscription?.BillingIntervalBillingCount !=
      subscription?.DeliveryIntervalDeliveryCount;

  const isBillingOrUpdatingOrScheduled =
    subscription?.IsBilling ||
    subscription?.IsUpdating ||
    subscription?.IsBillingScheduled;
  const isDynamicBaBLine = !!isDynamicLine(line);
  const isClassicBaBLine = isClassicBabLine(line);
  const nonDynamicBaBLines = subscription.Lines.filter(
    (line) => !line.HideLine || !line.OneTimeAddon,
  ).filter((line) => !line.OneTimeAddon);

  const isLastLine = nonDynamicBaBLines.length === 1;

  const isFreeProduct = itemIsFreeProduct(
    line,
    customerPortalSettings?.preventEditFreeItems,
  );

  const isShuffle =
    getCustomAttribute('_bundleType', line.CustomAttributes) === 'shuffle';
  const isQtyEditable =
    isFreeProduct ||
    scLineModAllowed(line, SC_LINE_MODS.QUANTITY_EDIT, SOURCE.dynamo);
  const isRemovable = scLineModAllowed(
    line,
    SC_LINE_MODS.REMOVAL,
    SOURCE.dynamo,
  );

  const shouldHideQtyButtons =
    isFreeProduct ||
    isCancelled ||
    !isQtyEditable ||
    isPaused ||
    isPrepaid ||
    line.PrepaidV2 ||
    isBillingOrUpdatingOrScheduled ||
    isDynamicBaBLine ||
    isClassicBaBLine ||
    isShuffle;

  const isBaBIgnoreOutOfStockEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.LAUNCH_BAB_IGNORE_OUT_OF_STOCK,
  );
  const shouldDisableIncrease =
    isBaBIgnoreOutOfStockEnabled &&
    ignoreBaBOutOfStockAllowance &&
    line.IsSoldOut &&
    line.SoldOutPolicy === 'CONTINUE';

  return line.IsSoldOut && line.SoldOutPolicy === 'DENY' ? (
    <Tag borderRadius="full">{t('tables.sold_out')}</Tag>
  ) : (isBaB && line.Quantity > 1) || !isBaB ? (
    <QuantityButton
      hideButtons={shouldHideQtyButtons}
      value={line.Quantity}
      isDecreaseDisabled={
        hasMinQuantityItemsInSubscription(line, subscription as Subscription) ||
        (isLastLine && line.Quantity === 1 && !line.OneTimeAddon) ||
        (line.Quantity === 1 && !isRemovable)
      }
      isIncreaseDisabled={shouldDisableIncrease}
      onIncrease={(quantity) =>
        handleOnQuantityChange(quantity, line, 'INCREASE')
      }
      onDecrease={(quantity) =>
        handleOnQuantityChange(quantity, line, 'DECREASE')
      }
    />
  ) : null;
};

const TableRow = ({
  image,
  line,
  shuffleData,
  subscription,
  sellingPlans,
  handleUpdateProduct,
  handleUpdateVariant,
  handleRemoveClick,
  handleSkipClick,
  handleUnskipClick,
  handleUpdateBundle,
  swapProductForBundle,
  handleOnQuantityChange,
  babProductVariants,
  shopConfiguration,
  disableCurrencySensitiveActions = false,
  disableSkipShuffleProduct,
}) => {
  const {
    customerPortalSettings,
    enableShuffleUpcomingProducts,
    baBEditAutoOpened,
    setBaBEditAutoOpened,
  } = useGlobalContext();

  const [showEditShuffleModal, setShowEditShuffleModal] = useState(false);

  const { t } = useTranslation();
  const hasPrepaidV2SwapEnabled =
    line.ProductStatus && line.ProductStatus === 'ACTIVE';

  const [showEditMyBoxModal, setShowEditMyBoxModal] = useState(false);

  const [productSlidePanel, setProductSlidePanel] = React.useState<{
    show: boolean;
    product: ProductSwapTypes | null;
    productId: string;
    variantId: string;
    isFreeProduct: boolean;
  }>({
    show: false,
    product: null,
    productId: '',
    variantId: '',
    isFreeProduct: false,
  });

  const closeSlidePanel = () => {
    setProductSlidePanel({
      show: false,
      product: null,
      productId: '',
      variantId: '',
      isFreeProduct: false,
    });
    setShowEditMyBoxModal(false);
  };

  const openSlidePanel = (
    productType: ProductSwapTypes,
    productId: string,
    variantId: string,
    isFreeProduct = false,
  ) => {
    !isFreeProduct &&
      setProductSlidePanel({
        show: true,
        product: productType,
        productId,
        variantId,
        isFreeProduct,
      });
  };

  const dynamicBaBProductsLinesQty = useMemo(
    () =>
      uniq(
        subscription.Lines.map(
          ({ CustomAttributes }) =>
            CustomAttributes?.find(({ key }) => key === '_bundleUid')?.value,
        ).filter(Boolean),
      ).length,
    [subscription.Lines],
  );

  useEffect(() => {
    const { autoOpenBaB } = customerPortalSettings || {};

    const classicBaBProductsLinesQty = subscription?.Lines?.filter(
      ({ CustomAttributes }) =>
        CustomAttributes?.find(({ key }) => key === '_bundleBoxPrice'),
    ).length;

    const pageAccessedByNavigate =
      (window?.performance?.navigation &&
        window?.performance?.navigation?.type === 0) ||
      window?.performance
        ?.getEntriesByType('navigation')
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ?.map((nav) => nav?.type)
        .includes('navigate');

    const isOnlyOneBaBInSubscription =
      dynamicBaBProductsLinesQty + classicBaBProductsLinesQty === 1;

    if (
      autoOpenBaB &&
      isOnlyOneBaBInSubscription &&
      !baBEditAutoOpened &&
      pageAccessedByNavigate
    ) {
      setBaBEditAutoOpened(true);
      setShowEditMyBoxModal(true);
    }
  }, [
    baBEditAutoOpened,
    customerPortalSettings,
    subscription,
    dynamicBaBProductsLinesQty,
    setBaBEditAutoOpened,
  ]);

  const isPrepaid =
    !!subscription?.BillingIntervalBillingCount &&
    !!subscription?.DeliveryIntervalDeliveryCount &&
    subscription?.BillingIntervalBillingCount !=
      subscription?.DeliveryIntervalDeliveryCount;

  const isBillingOrUpdatingOrScheduled =
    subscription?.IsBilling ||
    subscription?.IsUpdating ||
    subscription?.IsBillingScheduled;
  const isActive = subscription?.SubscriptionStatus === 'ACTIVE';

  const bundleProductId = getCustomAttribute(
    '_bundleProductId',
    line.CustomAttributes,
  );
  const isAddon = getCustomAttribute('_addon', line.CustomAttributes);

  const isClassicBundle =
    getCustomAttribute('_bundleType', line.CustomAttributes) === 'classic';
  const isShuffle =
    getCustomAttribute('_bundleType', line.CustomAttributes) === 'shuffle';

  const isBaB = !isShuffle && !isClassicBundle && bundleProductId && !isAddon;

  const hasDynamicSize =
    isBaB &&
    !!getCustomAttribute('_bundleBoxMinSize', line.CustomAttributes) &&
    !!getCustomAttribute('_bundleBoxMaxSize', line.CustomAttributes);

  const totalItems = hasDynamicSize
    ? line.CustomAttributes.reduce((acc, attribute) => {
        if (attribute.key.startsWith('_pv_')) {
          return acc + parseInt(attribute.value);
        }
        return acc;
      }, 0)
    : 0;

  let subtitle;
  if (bundleProductId && !isAddon) {
    subtitle = getCustomAttribute('_bundleLabel', line.CustomAttributes);
  }

  // add visible custom attributes if isBaB
  if (isBaB || isClassicBundle) {
    const attributesString = `(${getUnhiddenCustomAttributesString(
      line?.CustomAttributes || [],
    )})`;
    subtitle = (
      <>
        {hasDynamicSize
          ? t(`bundles.dynamicSize.selection`, {
              count: totalItems,
              item: t(
                `bundles.dynamicSize.item.${
                  totalItems === 1 ? 'singular' : 'plural'
                }`,
              ),
            })
          : subtitle}
        <span className="bab-items hidden">{attributesString}</span>
      </>
    );
  }
  subtitle =
    subtitle || (line.VariantTitle && line.VariantTitle !== '-')
      ? line.VariantTitle
      : '';

  const nonDynamicBaBLines = subscription.Lines.filter(
    (line) =>
      !line.OneTimeAddon &&
      !line.CustomAttributes?.find(
        ({ key, value }) =>
          key === '_bundleUid' ||
          (key === '_bundleType' && value === 'dynamicBaB'),
      ),
  );

  const isLastLine =
    nonDynamicBaBLines.length + dynamicBaBProductsLinesQty === 1;

  const isDynamicBaBLine = !!isDynamicLine(line);

  const babLines = getBaBLines(line, subscription, babProductVariants);

  const shouldDisableDecrease = (lineQty: number): boolean =>
    !!(
      hasMinQuantityItemsDynamically(line, subscription as Subscription) ||
      (lineQty === 1 && !line.OneTimeAddon)
    );

  return (
    <>
      <ProductProvider
        image={image}
        sellingPlans={sellingPlans}
        product={line as Product}
        updateProduct={handleUpdateProduct}
        updateVariant={handleUpdateVariant}
        removeProduct={handleRemoveClick}
        updateBundle={handleUpdateBundle}
        swapProductForBundle={swapProductForBundle}
        closeSlidePanel={closeSlidePanel}
        openSlidePanel={openSlidePanel}
        productSlidePanel={productSlidePanel}
        setShowEditMyBoxModal={setShowEditMyBoxModal}
        setShowCurateShuffleModal={setShowEditShuffleModal}
        isDecreaseDisabled={shouldDisableDecrease}
        isLastLine={isLastLine}
        subscriptionContainsGift={!!subscription.ContainsGift}
      >
        <ProductRow
          line={line}
          image={image}
          subtitle={subtitle}
          isBaB={isBaB}
          isClassicBundle={isClassicBundle}
          subscription={subscription}
          handleOnQuantityChange={handleOnQuantityChange}
          isDynamicBaBLine={isDynamicBaBLine}
          isActive={isActive}
          isPrepaid={isPrepaid}
          hasPrepaidV2SwapEnabled={hasPrepaidV2SwapEnabled}
          isBillingOrUpdatingOrScheduled={isBillingOrUpdatingOrScheduled}
          showEditMyBoxModal={showEditMyBoxModal}
          shopConfiguration={shopConfiguration}
          openSlidePanel={openSlidePanel}
          bundleProductId={bundleProductId}
          setShowEditMyBoxModal={setShowEditMyBoxModal}
          setShowCurateShuffleModal={setShowEditShuffleModal}
          showCurateShuffleModal={showEditShuffleModal}
          shuffleData={shuffleData}
          disableSkipShuffleProduct={disableSkipShuffleProduct}
          disableCurrencySensitiveActions={disableCurrencySensitiveActions}
          handleSkipShuffleProduct={handleSkipClick}
          handleUnskipShuffleProduct={handleUnskipClick}
        />
      </ProductProvider>
      {(isBaB || isClassicBundle) &&
        babLines.map(({ variantId, title, quantity, scLine, image }) => {
          if (!scLine) return null;
          return (
            <tr
              key={variantId}
              className="bundle-table__body-row bundle-table__body-row--bab-child"
            >
              {babProductVariants.length > 0 ? (
                <>
                  <td>
                    <BundleItemContainer>
                      <BundleImageContainer>
                        {image ? (
                          <BundleVariantImage
                            alt={title}
                            src={getOptimizedImageURL({
                              url: image,
                              width: '128',
                            })}
                          />
                        ) : (
                          <ImageEmptyState
                            className="bundle-img-empty-state"
                            width="32px"
                            height="32px"
                          />
                        )}
                      </BundleImageContainer>
                      <BundleProductLineName>{title}</BundleProductLineName>
                    </BundleItemContainer>
                  </td>
                  <td>
                    <BundleProductLineQuantity>
                      {line.BaBOutOfStockVariants?.includes(variantId) &&
                      shopConfiguration?.ShopOversell !== 'ALLOW' ? (
                        <Tag borderRadius="full">{t('tables.sold_out')}</Tag>
                      ) : (
                        <>
                          {quantity}
                          {line.Quantity > 1 && (
                            <Tag borderRadius="full">x {line.Quantity}</Tag>
                          )}
                        </>
                      )}
                    </BundleProductLineQuantity>
                  </td>
                  {isDynamicBaBLine && (
                    <>
                      <td>
                        <PricePerItem line={scLine} />
                      </td>
                      <td>
                        <ItemTotal line={scLine} />
                      </td>
                    </>
                  )}
                </>
              ) : (
                <td>
                  <Stack>
                    <Skeleton height="20px" />
                  </Stack>
                </td>
              )}
            </tr>
          );
        })}
      {shuffleData?.upcomingProducts?.length > 0 &&
        enableShuffleUpcomingProducts && (
          <>
            <tr className="bundle-table__body-row bundle-table__body-row--bundle-title">
              <td>
                <BundleItemContainer>
                  <BundleItemTitle>
                    {shuffleData.upcomingProducts.length > 1
                      ? t('tables.upcoming_products')
                      : t('tables.upcoming_product')}
                  </BundleItemTitle>
                  <BundleItemTitle>
                    <button
                      type="button"
                      className="bundleapp-btn--text"
                      onClick={() => setShowEditShuffleModal(true)}
                    >
                      {t('tables.curate_upcoming')}
                    </button>
                  </BundleItemTitle>
                </BundleItemContainer>
              </td>
            </tr>
            {shuffleData.upcomingProducts.map(
              ({
                productTitle,
                variantTitle,
                variantId,
                quantity,
                variantImage,
              }) => (
                <tr
                  key={variantId}
                  className="bundle-table__body-row bundle-table__body-row--shuffle-child"
                >
                  <td>
                    <BundleItemContainer>
                      <BundleImageContainer>
                        {variantImage ? (
                          <BundleVariantImage
                            alt={cleanDefaultDisplayName(
                              `${productTitle} - ${variantTitle}`,
                            )}
                            src={getOptimizedImageURL({
                              url: variantImage,
                              width: '128',
                            })}
                          />
                        ) : (
                          <ImageEmptyState
                            className="bundle-img-empty-state"
                            width="32px"
                            height="32px"
                          />
                        )}
                      </BundleImageContainer>
                      <BundleProductLineName>
                        {cleanDefaultDisplayName(
                          `${productTitle} - ${variantTitle}`,
                        )}
                      </BundleProductLineName>
                    </BundleItemContainer>
                  </td>
                  <td>
                    <BundleProductLineQuantity>
                      {quantity}
                    </BundleProductLineQuantity>
                  </td>
                </tr>
              ),
            )}
          </>
        )}
    </>
  );
};

const PricePerItem = ({ line }) => {
  const originalPricePerUnit = get(line, 'PricingPolicyBasePriceAmount')
    ? parseFloat(get(line, 'PricingPolicyBasePriceAmount', '0'))
    : null;
  const pricePerUnit = parseFloat(get(line, 'CurrentPriceAmount', '0'));

  return (
    <>
      {originalPricePerUnit && originalPricePerUnit > pricePerUnit ? (
        <StrikedPrice>{line.FormattedPricingPolicyBasePrice}</StrikedPrice>
      ) : null}
      {line.FormattedItemPrice}
    </>
  );
};

const ItemTotal = ({ line }) => {
  const originalPricePerUnit = get(line, 'PricingPolicyBasePriceAmount')
    ? parseFloat(get(line, 'PricingPolicyBasePriceAmount', '0'))
    : null;
  const lineDiscountedPrice = parseFloat(get(line, 'CurrentPriceAmount', '0'));
  return (
    <TotalLabel>
      {originalPricePerUnit && originalPricePerUnit > lineDiscountedPrice ? (
        <StrikedPrice>
          {line.FormattedPricingPolicyBasePriceMultipliedQuantiy}
        </StrikedPrice>
      ) : null}
      {line.FormattedLineTotal}
    </TotalLabel>
  );
};

const BundleItemContainer = styled.div`
  display: flex;
  padding-left: 32px;
`;

const BundleItemTitle = styled.h3.attrs({
  className: 'awt-bab-product-title',
})`
  //on mobile screen
  @media (max-width: ${Breakpoints.sm}) {
    font-size: 12px;
  }
  font-size: 14px;
  display: flex;
  align-items: center;
  padding-right: 6px;
  margin-bottom: 2px;
`;

const BundleProductLineName = styled.h3.attrs({
  className: 'awt-bab-product-name',
})`
  //on mobile screen
  @media (max-width: ${Breakpoints.sm}) {
    font-size: 14px;
  }
  font-size: 16px;
  display: flex;
  align-items: center;
  font-weight: 700;
  margin-bottom: 2px;
`;

const BundleImageContainer = styled.div.attrs({
  className: 'awt-img-container',
  'data-testid': 'bundle-image-container',
})`
  position: relative;
  padding-right: 16px;
  display: inline-block;
`;

const BundleVariantImage = styled.img.attrs({
  className: 'awt-bab-variant-img',
})`
  width: 32px;
  border-radius: 8px;
  position: relative;
  min-width: 32px;
`;

const BundleProductLineQuantity = styled.div.attrs({
  className: 'awt-bab-product-line-qty',
})`
  text-align: center;
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const SkipButton = styled(Button)`
  padding: 0;
  height: 26px;
`;

const ProductRow = ({
  line,
  image,
  subtitle,
  isBaB,
  isClassicBundle,
  subscription,
  handleOnQuantityChange,
  isDynamicBaBLine,
  isActive,
  isPrepaid,
  hasPrepaidV2SwapEnabled,
  isBillingOrUpdatingOrScheduled,
  showEditMyBoxModal,
  showCurateShuffleModal,
  setShowCurateShuffleModal,
  shuffleData,
  disableSkipShuffleProduct,
  handleSkipShuffleProduct,
  handleUnskipShuffleProduct,
  shopConfiguration,
  openSlidePanel,
  bundleProductId,
  setShowEditMyBoxModal,
  disableCurrencySensitiveActions = false,
}) => {
  const { t } = useTranslation();
  const isAutorenew = !subscription.BillingPolicyMaxCycles;
  const isExpired = subscription.SubscriptionStatus === 'EXPIRED';

  const { customerPortalSettings, bundleIsReady } = useGlobalContext();
  const { openModal } = useProductContext() as ProductContext;
  const isOneTimeAddon = !!line.OneTimeAddon;
  const isFreeProduct = itemIsFreeProduct(
    line,
    customerPortalSettings?.preventEditFreeItems,
  );

  const hasPrepaidVersion = !!line.PrepaidVersion || !!line.PrepaidV2;
  let prepaidTerms: { position: number; orderQty: number };
  if (hasPrepaidVersion) {
    const prepaidOrders = line.PrepaidOrders || line.PrepaidV2Orders;
    const position =
      ((line?.SuccessfulBillingAttempts?.length || 0) + 1) % prepaidOrders;
    prepaidTerms = {
      position: position || prepaidOrders,
      orderQty: prepaidOrders,
    };
  }

  const shouldDisableChangeProductOptions =
    disableCurrencySensitiveActions ||
    customerPortalSettings?.productChange ===
      PRODUCT_CHANGE_OPTIONS.NOT_ALLOWED ||
    !scLineModAllowed(line, SC_LINE_MODS.SWAP_VARIANT, SOURCE.dynamo);

  const shouldBypassVariantSwap =
    (window?.bundleapp?.settings?.bypassVariantSwap ||
      customerPortalSettings?.skipVariantSection) &&
    !hasPrepaidVersion;

  const productTitleClickHandler = () => {
    if (disableCurrencySensitiveActions || !bundleIsReady) {
      return;
    }

    if (bundleProductId) {
      setShowEditMyBoxModal(true);
    } else {
      if (shouldDisableChangeProductOptions) {
        return;
      }
      if (shouldBypassVariantSwap) {
        openModal(ProductSwapTypes.PRODUCT);
      } else {
        openSlidePanel(
          ProductSwapTypes.PRODUCT,
          line.ProductId,
          line.VariantId,
          isFreeProduct,
        );
      }
    }
  };
  const getPricingPolicyCycleDiscountsLabel = (
    PricingPolicyCycleDiscounts: PricingPolicyCycleDiscount[] = [],
  ) => {
    let label = '';

    for (let i = 0; i < PricingPolicyCycleDiscounts?.length; i++) {
      const currentDiscount: PricingPolicyCycleDiscount =
        PricingPolicyCycleDiscounts[i];

      const haveNoFixedDiscount =
        currentDiscount.adjustmentType === FIXED_AMOUNT_DISCOUNT &&
        currentDiscount.adjustmentValue.amount === 0;
      const haveNoPercentDiscount =
        currentDiscount.adjustmentType === PERCENTAGE_DISCOUNT &&
        currentDiscount.adjustmentValue.percentage === 0;
      const haveNoDiscounts =
        PricingPolicyCycleDiscounts?.length === 1 &&
        (haveNoPercentDiscount || haveNoFixedDiscount);

      let discount =
        currentDiscount.adjustmentType === PERCENTAGE_DISCOUNT
          ? t('subscription.percentage_dynamic_discount', {
              discount: currentDiscount.adjustmentValue.percentage,
            })
          : t('subscription.fixed_amount_dynamic_discount', {
              discount: currencyFormatter.format(
                currentDiscount.adjustmentValue.amount,
                {
                  code: currentDiscount.adjustmentValue.currencyCode,
                },
              ),
            });

      if (i < PricingPolicyCycleDiscounts.length - 1) {
        const nextDiscount: PricingPolicyCycleDiscount =
          PricingPolicyCycleDiscounts[i + 1];
        discount += t('subscription.orders_dynamic_discount', {
          orderQty: nextDiscount.afterCycle - currentDiscount.afterCycle,
        });
      } else if (PricingPolicyCycleDiscounts.length > 1) {
        discount = t('subscription.then_dynamic_discount', {
          discount,
        });
      }
      if (haveNoDiscounts) {
        label = '';
      } else {
        label += discount;
      }
    }

    return label;
  };

  const pricingPolicyCycleDiscountsLabel = getPricingPolicyCycleDiscountsLabel(
    line.PricingPolicyCycleDiscounts,
  );

  const trClassname = `bundle-table__body-row pv_${line.VariantId} ${
    isBaB || isClassicBundle
      ? 'bundle-table__body-row--bab-parent'
      : 'bundle-table__body-row--standard'
  }`;

  const hideEditMyBoxOptions = shouldDisableChangeProductOptions && isBaB;

  return (
    <tr
      className={trClassname}
      key={line.SubscriptionContractLineId}
      data-testid="table-row"
    >
      <td className="body-row__product-detail" data-label={t('tables.product')}>
        <ProductDetail
          tooltipLabel={
            !hasPrepaidVersion ? pricingPolicyCycleDiscountsLabel : null
          }
          image={image}
          title={line.Title}
          subtitle={subtitle}
          imgBadgeText={line.Quantity > 1 ? line.Quantity : undefined}
          outOfStockItems={
            line?.BaBOutOfStockVariants &&
            line?.BaBOutOfStockVariants?.length > 0 &&
            t('tables.qty_sold_out', {
              quantity: line?.BaBOutOfStockVariants?.length,
            })
          }
          onItemClick={productTitleClickHandler}
          prepaidV2Terms={
            hasPrepaidVersion
              ? isActive
                ? t(`prepaid.prepaidOrderQty`, {
                    completedQty: prepaidTerms.position,
                    orderQty: prepaidTerms.orderQty,
                  })
                : t(`prepaid.expiredAfter`, {
                    orderQty: prepaidTerms.orderQty,
                  })
              : undefined
          }
          isAutorenewLabel={
            isAutorenew && !isExpired ? t('prepaid.autoRenews') : undefined
          }
          isOneTimeAddonLabel={
            isOneTimeAddon ? t('addons.one_time') : undefined
          }
        />
      </td>
      <td
        data-label={t('tables.quantity')}
        className="body-row__quantity bundleapp-sm-hidden"
      >
        <QuantityDataCell
          line={line}
          isBaB={isBaB}
          subscription={subscription}
          t={t}
          handleOnQuantityChange={handleOnQuantityChange}
        />
      </td>
      {!subscription.ContainsGift && (
        <td
          className="body-row__price"
          data-label={t('tables.price')}
          data-testid="price-line"
        >
          {isDynamicBaBLine ? null : <PricePerItem line={line} />}
          <QuantityButtonContainer>
            <QuantityDataCell
              line={line}
              isBaB={isBaB}
              subscription={subscription}
              t={t}
              handleOnQuantityChange={handleOnQuantityChange}
            />
          </QuantityButtonContainer>
        </td>
      )}
      {!subscription.ContainsGift && (
        <td
          data-testid="price-total"
          className="body-row__total bundleapp-sm-hidden"
          data-label={t('tables.total')}
        >
          {isDynamicBaBLine ? null : <ItemTotal line={line} />}
        </td>
      )}
      {isActive && !isPrepaid && (
        <>
          {((line.PrepaidV2 && hasPrepaidV2SwapEnabled) || !line.PrepaidV2) &&
            !isBillingOrUpdatingOrScheduled &&
            !disableCurrencySensitiveActions &&
            !hideEditMyBoxOptions && (
              <ProductOptions
                showEditMyBoxModal={showEditMyBoxModal}
                currentVariantId={line.VariantId}
                subscription={subscription}
                shopConfiguration={shopConfiguration}
              />
            )}
        </>
      )}
      {showCurateShuffleModal && (
        <CurateShuffleModal
          open={showCurateShuffleModal}
          setOpen={setShowCurateShuffleModal}
          loading={disableSkipShuffleProduct}
          handleSkip={handleSkipShuffleProduct}
          handleUnskip={handleUnskipShuffleProduct}
          shuffleData={shuffleData}
          lineId={line.SubscriptionContractLineId}
        />
      )}
    </tr>
  );
};

export default TableRow;
